import { Close, WarningAmberRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material';
import React from 'react';

type TDeleteConfirmationProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  // Preferred — pass a ReactNode tree for the modal body. Avoids the
  // dangerouslySetInnerHTML path entirely and lets callers use real JSX
  // (bold names, links, lists). When `body` is set, `content.mainText` /
  // `content.warningText` are ignored.
  body?: React.ReactNode;
  // Legacy — kept for back-compat with the 5+ existing callers (RIP,
  // projects table, admin tables, etc.) that pass raw HTML strings.
  // New callers should prefer `body`.
  content?: {
    mainText: string;
    warningText?: string;
    customContent?: React.JSX.Element;
  };
  // Show a yellow warning icon next to the title. Communicates
  // destructiveness instantly. Defaults to off so legacy callers don't
  // suddenly change shape.
  warning?: boolean;
  handleConfirmation: () => void;
  isLoading: boolean;
};
const DeleteConfirmation = (props: TDeleteConfirmationProps) => {
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="archive-dialog-title"
      aria-describedby="archive-dialog-description"
      PaperProps={{ sx: { minWidth: 360, maxWidth: 440 } }}
    >
      <DialogTitle
        id="archive-dialog-title"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {props.warning && (
            <WarningAmberRounded
              sx={{ color: 'warning.main' }}
              aria-hidden
            />
          )}
          <Box component="span">{props.title}</Box>
        </Box>
        <IconButton onClick={props.onClose} disabled={props.isLoading}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {props.body ? (
          <Box id="archive-dialog-description">{props.body}</Box>
        ) : props.content ? (
          <>
            <DialogContentText
              id="archive-dialog-description"
              dangerouslySetInnerHTML={{ __html: props.content.mainText }}
            />
            {props.content.warningText && (
              <DialogContentText
                sx={{ color: 'error.main', mt: 2 }}
                dangerouslySetInnerHTML={{
                  __html: props.content.warningText,
                }}
              />
            )}
            {props.content.customContent}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={props.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={props.handleConfirmation}
          color="error"
          variant="contained"
          autoFocus
          disabled={props.isLoading}
        >
          {props.isLoading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmation;
